import classes from './InvitedUserSetup.module.css';
import AuthPageWrapper from 'Components/Auth/AuthPageWrapper/AuthPageWrapper';
import { useGetUser } from 'Backend/users/getUser';
import UserForm from 'Components/Setup/User/UserSlider/UserForm';
import { useEffect, useState } from 'react';
import { APIUser } from 'Models/models';
import { toBase64 } from 'Lib/Utils';
import { Notification } from 'Components/UI_Library/Notification';
import Button from 'Components/UI_Library/FormElements/Button';
import { useUpdateUser } from 'Backend/users/updateUser';
import { useNavigate, useSearchParams } from 'react-router-dom';

/** This component acts an interim step for a brand new invited user to finish setting up their user profile */
const InvitedUserSetup = () => {
    const navigate = useNavigate();
    const [search] = useSearchParams();
    const newUser = search.get('newUser');
    const [initialLoad, setInitialLoad] = useState(true);
    const { data: user, isLoading } = useGetUser();
    const [userObject, setUserObject] = useState<APIUser>();
    const {
        mutate: mutateUser,
        isSuccess: updateUserSuccess,
        data: updateUserData,
        isLoading: updateUserLoading,
    } = useUpdateUser();

    // AVATAR
    const [avatar, setAvatar] = useState('');
    const [loadingAvatar, setLoadingAvatar] = useState(false);

    // NOTIFICATIONS
    const [notification, setNotification] = useState('');
    const [notificationType, setNotificationType] = useState<'success' | 'error'>();

    useEffect(() => {
        setInitialLoad(false);
        if (newUser && user) {
            setUserObject({ ...user, referral: 'Invited' });
        } else {
            setUserObject(user);
        }
    }, [isLoading]);

    const changeAvatar = async (selectedFiles: FileList | null) => {
        if (selectedFiles?.[0]) {
            const acceptedImageTypes = ['image/gif', 'image/jpeg', 'image/png'];
            if (!acceptedImageTypes.includes(selectedFiles[0].type)) {
                handleNotification(
                    'The file type you uploaded is not supported. Please choose a GIF, JPG, or PNG image.',
                    'error'
                );
            } else if (selectedFiles[0].size > 1000000) {
                handleNotification(
                    'The size limit for profile pictures is 10mb. Please choose a smaller file.',
                    'error'
                );
            } else {
                setLoadingAvatar(true);
                const base64Data = (await toBase64(selectedFiles[0])) as string;
                mutateUser({
                    file: base64Data,
                    fileType: selectedFiles[0].type,
                });
            }
        }
    };

    const updateUser = (updatedUser?: APIUser) => {
        if (!updatedUser?.country) {
            handleNotification('Please add a country of residence', 'error');
            return;
        }
        if (!updatedUser?.mobile) {
            handleNotification('Please add a mobile number', 'error');
            return;
        }
        if (updatedUser) {
            mutateUser(updatedUser);
            setUserObject(updatedUser);
        }
    };

    useEffect(() => {
        if (updateUserData && updateUserData?.id === userObject?.id) {
            setNotification('');
            if (updateUserData?.avatar) {
                setAvatar(`${updateUserData.avatar}?t=${new Date().valueOf()}`);
                if (loadingAvatar) {
                    handleNotification('User avatar successfully updated', 'success');
                    // Return here as we might want to update other user details before being redirected
                    return;
                }
            }
            if (updateUserSuccess) {
                if (user?.userSource === 'oktaExternal') {
                    return navigate('/download-app');
                }

                handleNotification('User successfully updated, redirecting to 2fa setup', 'success');
                setTimeout(() => {
                    navigate('/setup-2fa');
                }, 3000);
            } else {
                handleNotification('User could not be updated. Please contact support', 'error');
            }
        }
    }, [updateUserData, updateUserSuccess]);

    const handleNotification = (message: string, type: 'success' | 'error', timeout = 3000) => {
        setNotification(message);
        setNotificationType(type);
        setLoadingAvatar(false);
        setTimeout(() => {
            setNotification('');
            setNotificationType(undefined);
        }, timeout);
    };

    return (
        <AuthPageWrapper>
            <div className={classes.centered}>
                <div className={classes.heading}>
                    <h2>Complete your user profile</h2>
                </div>
                {notification && (
                    <Notification
                        message={notification}
                        type={notificationType}
                        extraClass={classes.notification}
                        handleCloseMsg={() => setNotification('')}
                    />
                )}
                <UserForm
                    update={(user) => setUserObject(user)}
                    user={userObject}
                    initialLoad={initialLoad}
                    changeAvatar={changeAvatar}
                    avatar={avatar}
                    loadingAvatar={loadingAvatar}
                    enableNonEditableFields
                />
                <Button
                    type="primary"
                    text="Save"
                    onClick={() => updateUser(userObject)}
                    disabled={updateUserLoading}
                    loading={updateUserLoading}
                    className={classes.saveButton}
                />
            </div>
        </AuthPageWrapper>
    );
};

export default InvitedUserSetup;
